import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Upload, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";

interface LickingCountyTextParserProps {
  onImportComplete: () => void;
}

interface ImportResult {
  success: number;
  failed: number;
  failedRecords: Array<{ line: number; error: string }>;
}

export function LickingCountyTextParser({ onImportComplete }: LickingCountyTextParserProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [pastedText, setPastedText] = useState("");
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  const parseDate = (dateStr: string): string | null => {
    if (!dateStr || dateStr.trim() === '') return null;
    
    // Already in YYYY-MM-DD format
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateStr;
    }
    
    // Format: YYYYMMDD (8 digits)
    if (dateStr.match(/^\d{8}$/)) {
      const year = dateStr.substring(0, 4);
      const month = dateStr.substring(4, 6);
      const day = dateStr.substring(6, 8);
      return `${year}-${month}-${day}`;
    }
    
    return null;
  };

  const extractAmount = (amountStr: string): number | null => {
    if (!amountStr) return null;
    const cleaned = amountStr.replace(/[$,]/g, '');
    const amount = parseFloat(cleaned);
    return isNaN(amount) ? null : amount;
  };

  const analyzeOwnerFinance = (grantee: string, grantor: string): { 
    isOwnerFinance: boolean; 
    score: number; 
    reasons: string[] 
  } => {
    const reasons: string[] = [];
    let score = 0;

    const bankKeywords = [
      'BANK', 'MORTGAGE', 'LENDING', 'FINANCE', 'CREDIT UNION', 
      'FEDERAL', 'SAVINGS', 'LOAN', 'FSB', 'NA'
    ];
    
    const individualIndicators = [
      /\s[A-Z]\s/, // Middle initial
      /TRUST/i,
      /LLC$/i,
      /L L C$/i,
    ];

    const granteeUpper = grantee.toUpperCase();
    const grantorUpper = grantor.toUpperCase();

    // Check if grantee is likely a bank
    const granteeIsBank = bankKeywords.some(keyword => granteeUpper.includes(keyword));
    if (granteeIsBank) {
      reasons.push('Grantee appears to be a financial institution');
      return { isOwnerFinance: false, score: 0, reasons };
    }

    // Check if grantor is likely a bank
    const grantorIsBank = bankKeywords.some(keyword => grantorUpper.includes(keyword));
    if (grantorIsBank) {
      reasons.push('Grantor is likely a bank (institutional lender)');
      score -= 50;
    }

    // Check for individual/trust indicators
    const granteeIsIndividual = individualIndicators.some(pattern => pattern.test(grantee));
    const grantorIsIndividual = individualIndicators.some(pattern => pattern.test(grantor));

    if (granteeIsIndividual && grantorIsIndividual) {
      reasons.push('Both parties appear to be individuals or trusts');
      score += 60;
    }

    // Check for LLC to individual
    if (/L L C|LLC/i.test(grantor) && granteeIsIndividual) {
      reasons.push('LLC selling to individual via land contract');
      score += 40;
    }

    // Check for same last names (family transfer)
    const granteeLastName = grantee.split(/\s+/)[0];
    const grantorLastName = grantor.split(/\s+/)[0];
    if (granteeLastName === grantorLastName && granteeLastName.length > 2) {
      reasons.push('Same last name detected (possible family transfer)');
      score += 30;
    }

    return {
      isOwnerFinance: score >= 50,
      score: Math.max(0, Math.min(100, score)),
      reasons
    };
  };

  const parseLickingCountyText = (text: string): any[] => {
    const lines = text.trim().split('\n');
    const recordsMap = new Map<string, any>();

    for (const line of lines) {
      if (!line.trim()) continue;

      const fields = line.split('\t');
      if (fields.length < 7) continue;

      // Skip header or invalid rows
      const rowNum = fields[0]?.trim();
      if (!rowNum || isNaN(parseInt(rowNum))) continue;

      const lastName = fields[1]?.trim() || '';
      const firstName = fields[2]?.trim() || '';
      const partyType = fields[3]?.trim() || '';
      const otherPartyName = fields[4]?.trim() || '';
      const docType = fields[5]?.trim() || '';
      const documentNumber = fields[6]?.trim() || '';
      const relatedDocNumber = fields[8]?.trim() || '';
      const propertyDescription = fields[9]?.trim() || '';
      const filingDate = fields[10]?.trim() || '';
      const amount = fields[11]?.trim() || '';
      const notes = fields[13]?.trim() || '';

      // Accept various land contract document types
      const landContractTypes = [
        'LAND CONTRACT',
        'CONTRACT FOR DEED',
        'INSTALLMENT CONTRACT',
        'LAND INSTALLMENT CONTRACT',
        'CONTRACT FOR SALE',
        'INSTALLMENT SALE CONTRACT',
        'REAL ESTATE CONTRACT'
      ];
      
      const isLandContract = landContractTypes.some(type => 
        docType.toUpperCase().includes(type) || docType.toUpperCase().includes('LAND CONTRACT')
      );
      
      if (!isLandContract) continue;
      if (!documentNumber) continue;

      // Build party name
      const partyName = firstName ? `${lastName} ${firstName}` : lastName;
      
      // Get or create record for this document
      let record = recordsMap.get(documentNumber);
      if (!record) {
        const parsedDate = parseDate(filingDate);
        
        record = {
          county: 'licking',
          document_number: documentNumber,
          filing_date: parsedDate,
          document_type: docType,
          grantor_name: '',
          grantee_name: '',
          grantee_address: null,
          property_address: null,
          legal_description: propertyDescription || null,
          loan_amount: extractAmount(amount),
          is_potential_owner_finance: false,
          confidence_score: 0,
          detection_reasons: [],
          address_needs_review: true,
          status: 'new',
          notes: notes || null,
          raw_data: {
            related_document: relatedDocNumber,
            parties: []
          }
        };
        recordsMap.set(documentNumber, record);
      }

      // Add party to raw data
      record.raw_data.parties.push({
        name: partyName,
        type: partyType,
        other_party: otherPartyName
      });

      // Update grantor/grantee names
      if (partyType === 'Grantor' && partyName) {
        if (record.grantor_name) {
          record.grantor_name += `; ${partyName}`;
        } else {
          record.grantor_name = partyName;
        }
      } else if (partyType === 'Grantee' && partyName) {
        if (record.grantee_name) {
          record.grantee_name += `; ${partyName}`;
        } else {
          record.grantee_name = partyName;
        }
      }

      // Also capture from "other party" field if we don't have names yet
      if (!record.grantor_name && partyType === 'Grantee' && otherPartyName) {
        record.grantor_name = otherPartyName;
      }
      if (!record.grantee_name && partyType === 'Grantor' && otherPartyName) {
        record.grantee_name = otherPartyName;
      }
    }

    // All imported records are pre-filtered land contracts/deeds of trust
    // so mark them all as owner finance
    const records = Array.from(recordsMap.values());
    records.forEach(record => {
      record.is_potential_owner_finance = true;
      record.confidence_score = 100;
      record.detection_reasons = ['Manually imported land contract'];
    });

    // Sort by filing date (oldest first)
    records.sort((a, b) => {
      if (!a.filing_date && !b.filing_date) return 0;
      if (!a.filing_date) return 1;
      if (!b.filing_date) return -1;
      return b.filing_date.localeCompare(a.filing_date);
    });

    console.log(`Parsed ${records.length} unique land contracts from ${lines.length} total rows`);

    return records;
  };

  const processText = async (text: string) => {
    setIsProcessing(true);
    setProgress(0);
    setImportResult(null);

    try {
      const records = parseLickingCountyText(text);
      
      if (records.length === 0) {
        toast.error("No valid land contract records found in the text");
        setIsProcessing(false);
        return;
      }

      console.log('Attempting to insert', records.length, 'records');
      
      const result: ImportResult = {
        success: 0,
        failed: 0,
        failedRecords: []
      };

      for (let i = 0; i < records.length; i++) {
        const record = records[i];
        setProgress(Math.round(((i + 1) / records.length) * 100));

        try {
          // Check for duplicate
          const { data: existing } = await supabase
            .from('county_research')
            .select('id, property_address')
            .eq('county', 'licking')
            .eq('document_number', record.document_number)
            .maybeSingle();

          if (existing) {
            // If record has an address, skip it (user has manually entered data)
            if (existing.property_address) {
              console.log(`Skipping duplicate with address: ${record.document_number}`);
              result.failed++;
              result.failedRecords.push({
                line: i + 1,
                error: 'Duplicate with existing address'
              });
              continue;
            } else {
              // Update existing record without address to get new confidence scores
              const { error } = await supabase
                .from('county_research')
                .update({
                  is_potential_owner_finance: record.is_potential_owner_finance,
                  confidence_score: record.confidence_score,
                  detection_reasons: record.detection_reasons
                })
                .eq('id', existing.id);

              if (error) {
                console.error('Update error for record', i, ':', error);
                result.failed++;
                result.failedRecords.push({
                  line: i + 1,
                  error: error.message
                });
              } else {
                result.success++;
              }
              continue;
            }
          }

          const { error } = await supabase
            .from('county_research')
            .insert(record);

          if (error) {
            console.error('Insert error for record', i, ':', error);
            result.failed++;
            result.failedRecords.push({
              line: i + 1,
              error: error.message
            });
          } else {
            result.success++;
          }
        } catch (err) {
          console.error('Error processing record', i, ':', err);
          result.failed++;
          result.failedRecords.push({
            line: i + 1,
            error: String(err)
          });
        }
      }

      setImportResult(result);
      
      if (result.success > 0) {
        toast.success(`Successfully imported ${result.success} records`);
        setPastedText("");
        onImportComplete();
      }
      
      if (result.failed > 0) {
        toast.warning(`${result.failed} records failed to import`);
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error(`Import failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsProcessing(false);
      setProgress(0);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      const text = e.target?.result as string;
      await processText(text);
    };
    reader.readAsText(file);
  };

  const handlePasteImport = () => {
    if (!pastedText.trim()) {
      toast.error("Please paste some text first");
      return;
    }
    processText(pastedText);
  };

  return (
    <div className="space-y-4">
      {importResult && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Import completed: {importResult.success} succeeded, {importResult.failed} failed
            {importResult.failedRecords.length > 0 && (
              <div className="mt-2 text-sm">
                Failed records: {importResult.failedRecords.map(r => `Line ${r.line}`).join(', ')}
              </div>
            )}
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Licking County Land Contract Import</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Upload Text File</label>
            <div className="flex items-center gap-2">
              <input
                type="file"
                accept=".txt"
                onChange={handleFileUpload}
                disabled={isProcessing}
                className="block w-full text-sm"
              />
              <Upload className="h-4 w-4" />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Or Paste Text Data</label>
            <Textarea
              placeholder="Paste Licking County land contract data here..."
              value={pastedText}
              onChange={(e) => setPastedText(e.target.value)}
              disabled={isProcessing}
              rows={10}
              className="font-mono text-xs"
            />
            <Button 
              onClick={handlePasteImport} 
              disabled={isProcessing || !pastedText.trim()}
              className="w-full"
            >
              Import Pasted Data
            </Button>
          </div>

          {isProcessing && (
            <div className="space-y-2">
              <Progress value={progress} />
              <p className="text-sm text-center text-muted-foreground">
                Processing... {progress}%
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          <strong>Expected Format:</strong> Tab-separated data from Licking County with columns for:
          Row#, Last Name, First Name, Grantor/Grantee, Other Party, Document Type, Document Number, etc.
          <br /><br />
          <strong>Features:</strong>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>Automatically detects potential owner-finance deals</li>
            <li>Sets address_needs_review flag for all records</li>
            <li>Skips duplicate document numbers</li>
            <li>Extracts loan amounts when available</li>
          </ul>
        </AlertDescription>
      </Alert>
    </div>
  );
}
